import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import flvjs from 'flv.js';
import { io } from 'socket.io-client';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';

const LiveMonitor = () => {
  const { streamId } = useParams();
  const { user } = useAuth();
  const [centre, setCentre] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isStreamError, setIsStreamError] = useState(false);
  const videoRef = useRef(null);
  const flvPlayerRef = useRef(null);
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [activeCam, setActiveCam] = useState('Main Cam');

  useEffect(() => {
    const handleGlobalError = (event) => {
      const isEarlyEOF = event.message?.includes('Early-EOF') || 
                         event.error?.message?.includes('Early-EOF') ||
                         event.reason?.message?.includes('Early-EOF');
      
      if (isEarlyEOF) {
        event.preventDefault();
        if (event.stopPropagation) event.stopPropagation();
        console.warn('📡 Stream Monitor: Suppressed Early-EOF runtime error to prevent crash overlay.');
        return true;
      }
    };

    window.addEventListener('error', handleGlobalError, true);
    window.addEventListener('unhandledrejection', handleGlobalError, true);
    
    return () => {
      window.removeEventListener('error', handleGlobalError, true);
      window.removeEventListener('unhandledrejection', handleGlobalError, true);
    };
  }, []);

  useEffect(() => {
    const fetchCentre = async () => {
      try {
        const { data } = await api.get(`/centres/${streamId}`);
        setCentre(data.data);
      } catch (error) {
        console.error("Error fetching centre details:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchChatHistory = async () => {
      try {
        const { data } = await api.get(`/chat/${streamId}/chat/messages`);
        setMessages(data.data);
      } catch (error) {
        console.error("Error fetching chat history:", error);
      }
    };

    fetchCentre();
    fetchChatHistory();
  }, [streamId]);

  useEffect(() => {
    if (!streamId) return;

    const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'http://localhost:4000';
    const token = localStorage.getItem('amps_token');
    
    const socket = io(SOCKET_URL, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      auth: { token }
    });
    
    socketRef.current = socket;

    socket.on('connect', () => {
      console.log('Connected to Chat Socket');
      socket.emit('join_live_chat', { 
        centreId: streamId, 
        guestName: user?.name || 'Admin' 
      });
    });

    socket.on('live_chat_message', (newMsg) => {
      setMessages(prev => [...prev, {
        ...newMsg,
        createdAt: newMsg.timestamp || newMsg.createdAt || new Date()
      }]);
    });

    socket.on('pinned_messages', (pinnedList) => {
      setMessages(prev => prev.map(msg => ({
        ...msg,
        isPinned: pinnedList.some(pm => pm._id === msg._id)
      })));
    });

    socket.on('message_deleted', ({ messageId }) => {
      setMessages(prev => prev.filter(msg => msg._id !== messageId));
    });

    socket.on('warning_alert', ({ message }) => {
      alert(message);
    });

    socket.on('connect_error', (err) => {
      console.error('Socket Connection Error:', err.message);
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.off('live_chat_message');
        socketRef.current.off('pinned_messages');
        socketRef.current.off('message_deleted');
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [streamId, user]);

  useEffect(() => {
    if (!(centre && centre.isLive && videoRef.current && flvjs.isSupported())) {
      return;
    }

    let cancelled = false;

    // Cleanup previous player
    if (flvPlayerRef.current) {
      try { flvPlayerRef.current.destroy(); } catch (e) {}
      flvPlayerRef.current = null;
    }

    const startPlayer = (streamToken) => {
      const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://localhost:4000/api';
      // SECURITY: gated by the short-lived server-issued stream token (?st=), not the login token.
      let streamUrl = `${apiBase}/centres/${centre._id}/stream.flv?st=${encodeURIComponent(streamToken)}`;
      if (activeCam !== 'Main Cam') {
        streamUrl = `${streamUrl}&view=${encodeURIComponent(activeCam)}`;
      }

      const player = flvjs.createPlayer({
        type: 'flv',
        url: streamUrl,
        isLive: true
      }, {
        enableWorker: false,
        enableStashBuffer: false
      });

      flvPlayerRef.current = player;
      player.attachMediaElement(videoRef.current);
      player.load();

      const playPromise = player.play();
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          console.error("Stream playback error:", error);
          setIsStreamError(true);
        });
      }

      player.on(flvjs.Events.ERROR, (errType, errDetail) => {
        console.error("FLV Error:", errType, errDetail);
        setIsStreamError(true);
      });
    };

    (async () => {
      try {
        const res = await api.get(`/centres/${centre._id}/stream-access`);
        if (cancelled) return;
        const streamToken = res.data?.token;
        if (!streamToken) { setIsStreamError(true); return; }
        startPlayer(streamToken);
      } catch (err) {
        if (cancelled) return;
        console.error("Stream access denied:", err?.response?.status);
        setIsStreamError(true);
      }
    })();

    const currentVideo = videoRef.current;
    return () => {
      cancelled = true;
      if (flvPlayerRef.current) {
        try {
          flvPlayerRef.current.pause();
          flvPlayerRef.current.unload();
          flvPlayerRef.current.detachMediaElement();
          flvPlayerRef.current.destroy();
        } catch (e) {
          console.warn("FLV Cleanup error:", e);
        }
        flvPlayerRef.current = null;
      }
      if (currentVideo) {
        currentVideo.removeAttribute('src');
        currentVideo.load();
      }
    };
  }, [centre, activeCam]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const togglePin = (msgId) => {
    const msg = messages.find(m => m._id === msgId);
    if (!msg || !socketRef.current) return;

    if (msg.isPinned) {
      socketRef.current.emit('unpin_live_chat_message', { 
        centreId: streamId, 
        messageId: msgId 
      });
    } else {
      socketRef.current.emit('pin_live_chat_message', { 
        centreId: streamId, 
        messageId: msgId,
        message: msg.message 
      });
    }
  };

  const handleDeleteMessage = (msgId) => {
    if (!socketRef.current) return;
    if (window.confirm("Are you sure you want to delete this message?")) {
      socketRef.current.emit('delete_message', { 
        centreId: streamId, 
        messageId: msgId 
      });
    }
  };

  const pinnedMessage = messages.find(m => m.isPinned);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAF9F6]">
        <div className="w-12 h-12 border-4 border-[#2A2858] border-t-transparent rounded-full animate-spin mb-4"></div>
        <p className="text-stone-500 font-bold font-serif animate-pulse text-lg tracking-widest uppercase">Initializing Monitor...</p>
      </div>
    );
  }

  return (
    <div className="p-8 bg-[#FAF9F6] min-h-screen">
      <div className="mb-8 flex justify-between items-end">
        <div>
          <h2 className="text-3xl font-bold text-[#2A2858] font-serif">Live Stream Monitor</h2>
          <p className="text-stone-500 mt-1">Real-time chat moderation and stream oversight</p>
        </div>
        <div className="flex gap-4">
          <div className="flex items-center gap-2 bg-green-50 px-4 py-2 rounded-xl border border-green-100">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
            <span className="text-xs font-bold text-green-700 uppercase tracking-widest">Live: {centre?.name || 'Darshan'}</span>
          </div>
          <div className="flex items-center gap-2 bg-stone-100 px-4 py-2 rounded-xl border border-stone-200">
            <span className="material-symbols-outlined text-sm text-stone-500">group</span>
            <span className="text-xs font-bold text-stone-700">1,248 Viewers</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Stream Player */}
        <div className="lg:col-span-2 space-y-6">
          <div className="aspect-video bg-black rounded-3xl overflow-hidden shadow-2xl relative border-4 border-white group">
            {centre?.isLive && !isStreamError ? (
              <video 
                ref={videoRef} 
                className="w-full h-full object-cover" 
                controls={false}
                autoPlay 
                muted
              />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-stone-900">
                <span className="material-symbols-outlined text-white/20 text-8xl mb-4">
                  {centre?.isLive ? 'sync_problem' : 'videocam_off'}
                </span>
                <p className="text-white/40 font-bold uppercase tracking-widest text-xs">
                  {centre?.isLive ? 'Connecting to Stream...' : 'Stream Offline'}
                </p>
                {isStreamError && (
                  <button 
                    onClick={() => window.location.reload()}
                    className="mt-4 px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-[10px] font-bold rounded-lg border border-white/10 uppercase tracking-widest transition-all"
                  >
                    Retry Connection
                  </button>
                )}
              </div>
            )}
            
            <div className="absolute top-6 left-6 flex items-center gap-3">
               <div className={`${centre?.isLive ? 'bg-red-600' : 'bg-stone-600'} text-white text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-widest flex items-center gap-1.5 shadow-lg`}>
                  <span className={`w-1.5 h-1.5 bg-white rounded-full ${centre?.isLive ? 'animate-pulse' : ''}`}></span>
                  {centre?.isLive ? 'Live' : 'Offline'}
               </div>
               {centre?.isLive && (
                 <div className="bg-black/50 backdrop-blur-md text-white text-[10px] font-bold px-3 py-1 rounded-full uppercase tracking-widest border border-white/20">
                    1080p • 60fps
                 </div>
               )}
            </div>
          </div>

          {/* Camera Selection Controls */}
          {centre?.cameras && centre.cameras.length > 0 && (
            <div className="flex gap-2 p-3 bg-white border border-[#E8B84B]/20 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-x-auto">
              <button
                onClick={() => {
                  setActiveCam('Main Cam');
                  setIsStreamError(false);
                }}
                className={`px-4 py-2.5 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all whitespace-nowrap flex items-center gap-1.5 ${
                  activeCam === 'Main Cam'
                    ? 'bg-[#2A2858] text-white shadow-md'
                    : 'bg-stone-50 text-stone-600 hover:bg-[#E8B84B]/10 hover:text-[#E8B84B] border border-stone-200'
                }`}
              >
                <span className="material-symbols-outlined text-sm">videocam</span> Main Cam
              </button>
              {centre.cameras.map((cam, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    setActiveCam(cam.angleName);
                    setIsStreamError(false);
                  }}
                  className={`px-4 py-2.5 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all whitespace-nowrap flex items-center gap-1.5 ${
                    activeCam === cam.angleName
                      ? 'bg-[#2A2858] text-white shadow-md'
                      : 'bg-stone-50 text-stone-600 hover:bg-[#E8B84B]/10 hover:text-[#E8B84B] border border-stone-200'
                  }`}
                >
                  <span className="material-symbols-outlined text-sm">videocam</span> {cam.angleName}
                </button>
              ))}
            </div>
          )}

          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-2xl border border-stone-100 shadow-sm">
               <div className="text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-1">Bitrate</div>
               <div className="text-xl font-bold text-stone-800">4.2 Mbps</div>
            </div>
            <div className="bg-white p-4 rounded-2xl border border-stone-100 shadow-sm">
               <div className="text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-1">Status</div>
               <div className="text-xl font-bold text-green-600">Excellent</div>
            </div>
            <div className="bg-white p-4 rounded-2xl border border-stone-100 shadow-sm">
               <div className="text-[10px] font-bold text-stone-400 uppercase tracking-widest mb-1">Dropped Frames</div>
               <div className="text-xl font-bold text-stone-800">0%</div>
            </div>
          </div>
        </div>

        {/* Chat Moderation Window */}
        <div className="flex flex-col h-[calc(100vh-200px)] bg-white rounded-3xl shadow-xl border border-stone-100 overflow-hidden">
          <div className="p-5 border-b border-stone-100 flex justify-between items-center bg-stone-50/50">
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-[#2A2858]">forum</span>
              <h3 className="font-bold text-stone-800">Live Chat Moderation</h3>
            </div>
            <button className="text-stone-400 hover:text-stone-600">
              <span className="material-symbols-outlined">settings</span>
            </button>
          </div>

          {/* Pinned Message Section */}
          <div className="p-4 bg-[#FFF8E1] border-b border-[#FDD835]/30">
            <div className="text-[9px] font-black text-[#856404] uppercase tracking-widest mb-1 flex items-center gap-1">
              <span className="material-symbols-outlined text-[14px]">push_pin</span>
              Pinned Announcement
            </div>
            {pinnedMessage ? (
              <div className="flex justify-between items-start gap-3">
                <p className="text-sm text-stone-800 font-medium leading-relaxed italic">
                  "{pinnedMessage.message}" — <span className="font-bold text-[#2A2858]">{pinnedMessage.guestName || pinnedMessage.userId?.name}</span>
                </p>
                <button onClick={() => togglePin(pinnedMessage._id)} className="text-[#856404] hover:text-red-600 transition-colors">
                  <span className="material-symbols-outlined text-[18px]">close</span>
                </button>
              </div>
            ) : (
              <p className="text-xs text-[#856404]/60 italic font-medium">No message currently pinned to the stream.</p>
            )}
          </div>

          {/* Chat Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar bg-stone-50/30">
            {messages.length > 0 ? (
              messages.map((msg) => (
                <div key={msg._id} className={`group relative flex items-start gap-3 p-3 rounded-2xl transition-all duration-300 ${msg.isPinned ? 'bg-[#FFF8E1] border border-[#FDD835]/20 shadow-sm' : 'hover:bg-white border border-transparent'}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 font-bold text-[10px] ${msg.isPinned ? 'bg-[#E8B84B] text-white' : 'bg-stone-200 text-stone-500'}`}>
                    {(msg.guestName || msg.userId?.name || 'U').charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-0.5">
                      <div className="flex items-center gap-1.5">
                        <h4 className={`text-[10px] font-black uppercase tracking-widest ${msg.isPinned ? 'text-[#856404]' : 'text-stone-800'}`}>
                          {msg.guestName || msg.userId?.name || 'Unknown User'}
                        </h4>
                      </div>
                      <span className="text-[8px] text-stone-400 font-bold">
                        {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <p className="text-stone-600 text-xs leading-snug break-words">
                      {msg.message}
                    </p>
                  </div>
                  
                  <div className="absolute top-2 right-2 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-all duration-200 bg-white/95 backdrop-blur-sm shadow-xl border border-stone-100 rounded-xl p-1.5 z-10">
                    <button 
                      onClick={() => togglePin(msg._id)}
                      className={`w-7 h-7 flex items-center justify-center rounded-lg transition-all ${msg.isPinned ? 'text-white bg-[#E8B84B]' : 'text-stone-400 hover:bg-stone-100 hover:text-stone-800'}`}
                      title={msg.isPinned ? "Unpin Message" : "Pin Message"}
                    >
                      <span className="material-symbols-outlined text-[18px]">push_pin</span>
                    </button>
                    <button 
                      onClick={() => handleDeleteMessage(msg._id)}
                      className="w-7 h-7 flex items-center justify-center rounded-lg text-stone-400 hover:bg-red-50 hover:text-red-600 transition-all" 
                      title="Delete Message"
                    >
                      <span className="material-symbols-outlined text-[18px]">delete</span>
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex flex-col items-center justify-center h-full opacity-20 py-20">
                <span className="material-symbols-outlined text-6xl mb-4">forum</span>
                <p className="font-bold uppercase tracking-widest text-xs">No Messages Yet</p>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="p-4 border-t border-stone-100 bg-stone-50/50">
            <div className="flex gap-2">
              <input 
                type="text" 
                placeholder="Send a mod announcement..." 
                className="flex-1 bg-white border border-stone-200 rounded-xl px-4 py-2 text-sm outline-none focus:border-[#2A2858]"
              />
              <button className="bg-[#2A2858] text-white w-10 h-10 rounded-xl flex items-center justify-center shadow-md hover:bg-[#14122B] transition-all">
                <span className="material-symbols-outlined">send</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveMonitor;
